import RBush from 'rbush';
import type { Point, Rect } from './camera';
import { obbBounds, type OBB } from './geometry';

/**
 * The engine's read-only view of an object (the document layer maps its own data to this): world OBB, paint rank
 * (higher is drawn above; children are always above their container) and effective parent.
 */
export type SceneObject = {
  id: string;
  type: string;
  obb: OBB;
  z: string;
  rank: number;
  parentId: string | null;
  locked: boolean;
  /** A thin object (connector): hit only near this world polyline, not anywhere in its box. */
  path?: readonly Point[];
};
type Entry = Rect & { id: string };

export class SpatialIndex {
  private tree = new RBush<Entry>();
  private entries = new Map<string, Entry>();

  load(objs: SceneObject[]): void {
    this.tree.clear();
    this.entries.clear();
    const all = objs.map((o) => this.entry(o));
    for (const e of all) this.entries.set(e.id, e);
    this.tree.load(all);
  }

  upsert(o: SceneObject): void {
    this.remove(o.id);
    const e = this.entry(o);
    this.entries.set(e.id, e);
    this.tree.insert(e);
  }

  remove(id: string): void {
    const e = this.entries.get(id);
    if (!e) return;
    this.tree.remove(e);
    this.entries.delete(id);
  }

  /** Ids whose axis-aligned bounds overlap the rectangle (broad phase: callers refine with the exact shape). */
  queryRect(r: Rect): string[] {
    return this.tree.search(r).map((e) => e.id);
  }

  private entry(o: SceneObject): Entry {
    return { id: o.id, ...obbBounds(o.obb) };
  }
}
